import React, { useEffect, useRef } from 'react';

import { useInvertedLocalize } from '../../hooks';

import loadTurnstileScript from './loadTurnstileScript';

export interface TurnstileWidgetProps {
  siteKey?: string;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'compact' | 'flexible';
  appearance?: 'always' | 'execute' | 'interaction-only';
  isDisabled?: boolean;
  onToken?: (token: string) => void;
}

const DEFAULT_TEST_SITE_KEY = '1x00000000000000000000AA';

function TurnstileWidget({
  siteKey = DEFAULT_TEST_SITE_KEY,
  theme = 'light',
  size = 'flexible',
  appearance = 'always',
  isDisabled = false,
  onToken,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Store onToken in a ref so the Turnstile callback always calls the
  // latest version without re-initialising the widget on every render.
  const onTokenRef = useRef(onToken);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  const l = useInvertedLocalize();

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    let isActive = true;

    loadTurnstileScript()
      .then(() => {
        if (!isActive) {
          return;
        }

        const el = containerRef.current;

        if (!el || !window.turnstile) {
          return;
        }

        const prev = widgetIdRef.current;

        if (prev) {
          window.turnstile.remove(prev);
          widgetIdRef.current = null;
        }

        el.innerHTML = '';

        const id = window.turnstile.render(el, {
          sitekey: siteKey,
          theme,
          size,
          appearance,
          callback: (token) => onTokenRef.current?.(token),
        });

        widgetIdRef.current = id ?? null;
      })
      .catch(() => {
        // silent for builder
      });

    // eslint-disable-next-line consistent-return
    return () => {
      isActive = false;
      const id = widgetIdRef.current;

      if (id && window.turnstile) {
        window.turnstile.remove(id);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, size, appearance, isDisabled]);

  return <div ref={containerRef} aria-label={l('Cloudflare Turnstile')} />;
}

export default TurnstileWidget;
